import React from 'react';
import { useInView } from 'react-intersection-observer';

import { callbackHooks } from '@nxte-nl/callback-hooks';
import { ErrorBoundary } from '@nxte-nl/error-boundary';

import { ProductCard, Props as ProductCardProps } from '../../components/ProductCard/ProductCard';
import { dataLayerHooks } from '../../hooks/dataLayerHooks/dataLayerHooks';
import { dataLayerV2Hooks } from '../../hooks/dataLayerHooks/dataLayerV2Hooks';
import { optionalCallbackHooks } from '../../hooks/optionalCallbackHooks/optionalCallbackHooks';
import { ProductType } from '../../types/apiEnums';
import {
  DataLayerProductCategoryV2,
  DataLayerViewItemList,
} from '../../types/dataLayer';

export type Props = ProductCardProps & {
  className?: string;
  id: string | null;
  isExsternalLink?: boolean;
  isRedeem?: boolean;
  isTopProduct?: boolean;
  itemListID?: string;
  onClick?: (id: string | null) => void;
  position: number;
};

export type UseTrackSelectItemCallback = {
  id?: string | null;
  isTopProduct?: boolean;
  itemListID?: string;
  path?: string | null;
  position: number;
  productType?: ProductType;
  title: string;
};

export const hooks = {
  useTrackViewItemList({
    productType,
    isTopProduct,
    trackViewItemList,
    inView,
    position,
    title,
    id,
    path,
    itemListID,
  }: {
    id?: string | null;
    inView: boolean;
    isTopProduct?: boolean;
    itemListID?: string;
    path?: string | null;
    position: number;
    productType?: ProductType;
    title: string;
    trackViewItemList: (item: DataLayerViewItemList) => void;
  }) {
    const detectItemCategory = hooks.useDetectItemCategory();
    React.useEffect(() => {
      if (inView && !isTopProduct) {
        trackViewItemList({
          item_id: id || path || '',
          item_name: title,
          index: position,
          item_category: detectItemCategory(productType),
          quantity: 1,
          item_list_id: itemListID || '',
          item_list_name: itemListID || '',
        });
      }
    }, [
      id,
      inView,
      path,
      position,
      title,
      trackViewItemList,
      isTopProduct,
      itemListID,
      detectItemCategory,
      productType,
    ]);
  },
  useDetectItemCategory() {
    return React.useCallback((productType?: ProductType) => {
      const isPhysical =
        productType
        && [ProductType.BLANK_GIFT_CARD_PACK, ProductType.GIFTCARD].includes(productType);
      const isDigital = productType === ProductType.DONATION;

      return isPhysical
        ? DataLayerProductCategoryV2.PHYSICAL
        : isDigital
          ? DataLayerProductCategoryV2.DIGITAL
          : DataLayerProductCategoryV2.ADDITIONAL;
    }, []);
  },
  useTrackSelectItemCallback({
    productType,
    isTopProduct,
    position,
    title,
    id,
    path,
    itemListID,
  }: UseTrackSelectItemCallback) {
    const detectItemCategory = hooks.useDetectItemCategory();
    const trackSelectItem = dataLayerV2Hooks.useTrackSelectItem();
    return React.useCallback(() => {
      if (!isTopProduct) {
        trackSelectItem({
          item_id: id || path || '',
          item_name: title,
          index: position,
          item_category: detectItemCategory(productType),
          quantity: 1,
          item_list_id: itemListID || '',
          item_list_name: itemListID || '',
        });
      }
    }, [detectItemCategory, id, isTopProduct, itemListID, path,
      position, productType, title, trackSelectItem]);
  },
};

export const ProductCardDataLayerContainer = React.memo((props: Props) => {
  const {
    id,
    path,
    position,
    title,
    onClick,
    isRedeem,
    productType,
    itemListID,
    isTopProduct,
  } = props;

  const onClickCallback = optionalCallbackHooks.useHandleClickWithId(id, onClick);

  const handleClick = callbackHooks.useWrapCallbacks([
    onClickCallback,

    dataLayerHooks.useProductClickCallback(
      {
        id: id || path || '',
        name: title,
        position,
      },
      isRedeem,
    ),
    hooks.useTrackSelectItemCallback({
      productType,
      isTopProduct,
      position,
      title,
      id,
      path,
      itemListID,
    }),
  ]);

  const trackViewItemList = dataLayerV2Hooks.useTrackViewItemListDispatch();

  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  hooks.useTrackViewItemList({
    productType,
    isTopProduct,
    trackViewItemList,
    inView,
    position,
    title,
    id,
    path,
    itemListID,
  });

  return (
    <ErrorBoundary>
      <div data-product-id={props.id} ref={ref}>
        <ProductCard {...props} onClick={handleClick} />
      </div>
    </ErrorBoundary>
  );
});

ProductCardDataLayerContainer.displayName = 'ProductCardDataLayerContainer';
